import { useEffect, useRef, useState } from "react";

const WS_URL = `${process.env.BUN_PUBLIC_WS_BASE_URL}/v1/ws/connect`;
const MAX_BACKOFF_MS = 30_000;

export type WsStatus = "connected" | "connecting" | "disconnected";

export interface NeighborUser {
  user_id: string;
  username?: string;
  current_club_id?: string | null;
  current_club_name?: string | null;
  lat: number;
  lng: number;
  speed_kmph?: number;
  heading: number;
}

interface UseMapWsOptions {
  onNeighborUpdate?: (users: NeighborUser[]) => void;
}

export function useMapWs({ onNeighborUpdate }: UseMapWsOptions = {}) {
  const [status, setStatus] = useState<WsStatus>("disconnected");
  const wsRef = useRef<WebSocket | null>(null);
  const backoffRef = useRef(1_000);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmounted = useRef(false);
  const callbackRef = useRef(onNeighborUpdate);
  callbackRef.current = onNeighborUpdate;

  function sendViewportUpdate(lat: number, lng: number, radiusMeters: number) {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          action: "viewport_update",
          payload: { lat, lng, radius_meters: radiusMeters },
        }),
      );
    }
  }

  function connect() {
    if (unmounted.current) return;
    setStatus("connecting");
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      if (unmounted.current) return ws.close();
      backoffRef.current = 1_000;
      setStatus("connected");
    };

    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.action === "neighbor_update" && Array.isArray(msg.payload)) {
          callbackRef.current?.(msg.payload);
        }
      } catch {}
    };

    ws.onclose = () => {
      if (unmounted.current) return;
      setStatus("disconnected");
      reconnectTimer.current = setTimeout(() => {
        backoffRef.current = Math.min(backoffRef.current * 2, MAX_BACKOFF_MS);
        connect();
      }, backoffRef.current);
    };

    ws.onerror = () => ws.close();
  }

  useEffect(() => {
    unmounted.current = false;
    connect();
    return () => {
      unmounted.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, []);

  return { status, sendViewportUpdate };
}
